import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip,
} from 'recharts';
import { getPlayers, getIndividualProgressWeeks, getIndividualProgress } from '../api';
import PageChrome from '../components/PageChrome';

// The 6 selectable lines — the 5 pillar scores plus Overall, confirmed 2026-08-05 (not each
// pillar's own individual sub-KPIs — "for physical, it means the score calculated for only
// that week", i.e. one number per pillar, with Overall called out as an addition on top of
// "all KPIs" rather than one of them).
const METRICS = [
  { key: 'physical',     label: 'Physical',     color: '#38bdf8' },
  { key: 'technical',    label: 'Technical',    color: '#22c55e' },
  { key: 'tactical',     label: 'Tactical',     color: '#a855f7' },
  { key: 'identity',     label: 'Identity',     color: '#f59e0b' },
  { key: 'availability', label: 'Availability', color: '#ef4444' },
  { key: 'overall',      label: 'Overall',      color: '#000000' },
];

const f0 = v => (v == null || isNaN(v) ? '—' : Math.round(v));

// Line style per player when multiple players share one chart (2026-08-06) — color still
// encodes the metric (same 6 colors as the toggle legend), player is distinguished by stroke
// pattern instead, solid first (so a single selected player looks identical to before this
// change). Cycles if more than 6 players are ever selected at once.
const PLAYER_DASH_PATTERNS = ['', '6 3', '2 3', '9 3 2 3', '1 3', '10 2 2 2'];

// ── Generic checkbox multi-select dropdown — same shape as MatchChronic.js's MultiSelect,
// generalized for both the player and week pickers here. ──────────────────────────────────
function MultiSelect({ options, selected, onChange, placeholder, minWidth = 220 }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  useEffect(() => {
    const h = e => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const toggle = val => onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val]);
  const buttonLabel = selected.length === 0
    ? (placeholder || 'Select…')
    : selected.length === 1
      ? (options.find(o => o.value === selected[0])?.label ?? selected[0])
      : `${selected.length} selected`;
  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)} style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px 7px 12px',
        borderRadius: 8, border: `1px solid ${selected.length > 0 ? '#3b82f6' : 'var(--border-strong)'}`,
        background: selected.length > 0 ? 'rgba(59,130,246,0.1)' : 'var(--bg-card)',
        color: selected.length > 0 ? 'var(--text-primary)' : 'var(--text-primary)',
        fontSize: '0.84rem', fontFamily: 'inherit', cursor: 'pointer', outline: 'none', minWidth,
      }}>
        <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{buttonLabel}</span>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}
          style={{ transform: open ? 'rotate(180deg)' : 'none', transition: '0.15s', flexShrink: 0 }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 300,
          background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 10,
          minWidth: Math.max(minWidth, 260), maxHeight: 320, overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.5)', padding: '5px 0' }}>
          {options.length === 0 && (
            <div style={{ padding: '10px 12px', fontSize: '0.8rem', color: 'var(--text-dimmed)' }}>Nothing to select yet.</div>
          )}
          {options.map(opt => (
            <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', cursor: 'pointer',
              fontSize: '0.82rem', color: 'var(--text-primary)',
              background: selected.includes(opt.value) ? 'rgba(59,130,246,0.07)' : 'transparent' }}>
              <input type="checkbox" checked={selected.includes(opt.value)} onChange={() => toggle(opt.value)} style={{ accentColor: '#3b82f6', flexShrink: 0 }} />
              {opt.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Toggleable KPI legend — same pill-toggle pattern as WellnessStaff.js's METRICS legend. ──
function MetricLegend({ hidden, onToggle }) {
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
      {METRICS.map(m => {
        const isHidden = hidden.has(m.key);
        return (
          <button key={m.key} type="button" onClick={() => onToggle(m.key)} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 999,
            border: `1px solid ${isHidden ? 'var(--border)' : m.color}`,
            background: isHidden ? 'transparent' : `${m.color}20`,
            color: isHidden ? 'var(--text-dimmed)' : m.color,
            fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
          }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: isHidden ? 'var(--text-dimmed)' : m.color, display: 'inline-block' }} />
            {m.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Tooltip — groups by player when more than one is on the chart, since dataKeys are
// per-(player, metric) and a flat list would read as "Physical, Physical, Physical…". ────────
function ProgressTooltip({ active, payload, label, playersById }) {
  if (!active || !payload || !payload.length) return null;
  const byPlayer = new Map();
  payload.forEach(p => {
    const arr = byPlayer.get(p.playerId) || [];
    arr.push(p);
    byPlayer.set(p.playerId, arr);
  });
  const multiPlayer = byPlayer.size > 1;
  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 8, padding: '8px 12px', fontSize: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}>
      <div style={{ fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>{label}</div>
      {[...byPlayer.entries()].map(([playerId, entries]) => (
        <div key={playerId} style={{ marginTop: multiPlayer ? 6 : 0 }}>
          {multiPlayer && <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)' }}>{playersById?.[playerId]?.name || playerId}</div>}
          {entries.map(p => (
            <div key={p.dataKey} style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-secondary)' }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: p.stroke, display: 'inline-block' }} />
              {p.metricLabel}: <strong style={{ color: 'var(--text-primary)' }}>{f0(p.value)}</strong>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// ── Player legend — dash-pattern swatches, only shown once >1 player is selected (with a
// single player, solid lines already look exactly like before this feature existed). ─────────
function PlayerLegend({ players }) {
  if (players.length < 2) return null;
  return (
    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
      <span style={{ fontSize: '0.72rem', color: 'var(--text-dimmed)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Players:</span>
      {players.map((p, i) => (
        <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
          <svg width="22" height="8"><line x1="0" y1="4" x2="22" y2="4" stroke="var(--text-muted)" strokeWidth="2.2" strokeDasharray={PLAYER_DASH_PATTERNS[i % PLAYER_DASH_PATTERNS.length]} /></svg>
          {p.name}
        </div>
      ))}
    </div>
  );
}

// ── Combined chart — every selected player on one graph, one Line per (player, visible metric).
// Color still encodes the metric (same as the pill legend); each player gets a fixed dash
// pattern from PLAYER_DASH_PATTERNS so identity survives even with several players/metrics
// overlaid together. ───────────────────────────────────────────────────────────────────────
function CombinedChart({ players, weeks, scoresByPlayerAndWeek, hiddenLines }) {
  const chartData = useMemo(() => weeks.map(w => {
    const row = { label: `Week ${w.weekNumber}`, opponent: w.opponentName || 'No match' };
    players.forEach(p => {
      const score = (scoresByPlayerAndWeek[p.id] || {})[w.scenarioId];
      METRICS.forEach(m => { row[`p${p.id}_${m.key}`] = score?.[m.key] ?? null; });
    });
    return row;
  }), [weeks, players, scoresByPlayerAndWeek]);

  const playersById = useMemo(() => Object.fromEntries(players.map(p => [p.id, p])), [players]);
  const card = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14 };

  return (
    <div style={{ ...card, padding: '18px 18px 8px', marginBottom: 12 }}>
      <div style={{ height: 320 }}>
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={chartData} margin={{ top: 8, right: 16, left: -8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
            <XAxis dataKey="label" tick={{ fill: 'var(--text-muted)', fontSize: 10, fontFamily: 'Inter, sans-serif' }} axisLine={false} tickLine={false} />
            <YAxis domain={[0, 100]} tick={{ fill: 'var(--text-muted)', fontSize: 10, fontFamily: 'Inter, sans-serif' }} axisLine={false} tickLine={false} />
            <Tooltip content={<ProgressTooltip playersById={playersById} />} />
            {players.map((p, pi) => METRICS.map(m => !hiddenLines.has(m.key) && (
              <Line key={`${p.id}_${m.key}`} dataKey={`p${p.id}_${m.key}`}
                name={m.label} metricLabel={m.label} playerId={p.id}
                stroke={m.color} strokeWidth={2.2} strokeDasharray={PLAYER_DASH_PATTERNS[pi % PLAYER_DASH_PATTERNS.length]}
                dot={{ r: 3, fill: m.color }} activeDot={{ r: 5 }} connectNulls={false} isAnimationActive={false} />
            )))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ── One player's table, aligned on the same week columns as the combined chart above ─────────
function PlayerTableSection({ player, weeks, scoresByPlayer, hiddenLines }) {
  const card = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14 };
  const TH = { padding: '9px 10px', fontSize: '0.67rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)', textAlign: 'center', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap', background: 'var(--bg-app)' };
  const THL = { ...TH, textAlign: 'left' };
  const TD_ = { padding: '9px 10px', fontSize: '0.82rem', color: 'var(--text-primary)', textAlign: 'center' };
  const TDL = { ...TD_, textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap' };
  const TDAVG = { ...TD_, fontWeight: 700, borderLeft: '1px solid var(--border)', borderRight: '1px solid var(--border)', background: 'var(--bg-app)' };
  const avgOf = m => {
    const vals = weeks.map(w => scoresByPlayer[w.scenarioId]?.[m.key]).filter(v => v != null && !isNaN(v));
    return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
  };

  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div style={{ width: 36, height: 36, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, background: 'var(--bg-app)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: 'var(--text-dimmed)' }}>
          {player.photo_url ? <img src={player.photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (player.name || '?').charAt(0).toUpperCase()}
        </div>
        <div>
          <div style={{ fontSize: '0.98rem', fontWeight: 700, color: 'var(--text-primary)' }}>{player.name}</div>
          {player.position && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{player.position}</div>}
        </div>
      </div>

      <div style={{ ...card, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 260 + weeks.length * 90 }}>
          <thead>
            <tr>
              <th style={THL}>Metric</th>
              <th style={{ ...TH, borderLeft: '1px solid var(--border)', borderRight: '1px solid var(--border)' }}>Avg</th>
              {weeks.map(w => (
                <th key={w.scenarioId} style={TH}>
                  Week {w.weekNumber}
                  <div style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, marginTop: 2, color: 'var(--text-dimmed)' }}>{w.opponentName || 'No match'}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {METRICS.map((m, i) => (
              <tr key={m.key} style={{ borderTop: '1px solid var(--border)', background: hiddenLines.has(m.key) ? 'var(--bg-app)' : 'transparent', opacity: hiddenLines.has(m.key) ? 0.5 : 1 }}>
                <td style={{ ...TDL, color: m.color }}>{m.label}</td>
                <td style={TDAVG}>{f0(avgOf(m))}</td>
                {weeks.map(w => {
                  const row = scoresByPlayer[w.scenarioId];
                  return <td key={w.scenarioId} style={TD_}>{f0(row?.[m.key])}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function IndividualProgress({ user, onNavigate }) {
  const [players, setPlayers] = useState([]);
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlayerIds, setSelectedPlayerIds] = useState([]);
  const [selectedScenarioIds, setSelectedScenarioIds] = useState([]);
  const [scores, setScores] = useState([]);
  const [loadingScores, setLoadingScores] = useState(false);
  const [hiddenLines, setHiddenLines] = useState(() => new Set());

  useEffect(() => {
    Promise.all([getPlayers(), getIndividualProgressWeeks()])
      .then(([pl, wk]) => {
        setPlayers((pl || []).filter(p => p.active !== false).sort((a, b) => (a.name || '').localeCompare(b.name || '')));
        setWeeks(wk || []);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedPlayerIds.length || !selectedScenarioIds.length) { setScores([]); return; }
    setLoadingScores(true);
    getIndividualProgress(selectedPlayerIds, selectedScenarioIds)
      .then(data => setScores(data || []))
      .catch(() => setScores([]))
      .finally(() => setLoadingScores(false));
  }, [selectedPlayerIds, selectedScenarioIds]);

  const toggleLine = useCallback(key => setHiddenLines(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  }), []);

  const selectedWeeksOrdered = useMemo(() =>
    weeks.filter(w => selectedScenarioIds.includes(w.scenarioId)).sort((a, b) => a.weekNumber - b.weekNumber),
    [weeks, selectedScenarioIds]);

  const selectedPlayersOrdered = useMemo(() =>
    selectedPlayerIds.map(id => players.find(p => p.id === id)).filter(Boolean),
    [players, selectedPlayerIds]);

  const scoresByPlayerAndWeek = useMemo(() => {
    const out = {};
    scores.forEach(s => {
      if (!out[s.player_id]) out[s.player_id] = {};
      out[s.player_id][s.scenario_id] = s;
    });
    return out;
  }, [scores]);

  const weekOptions = useMemo(() => weeks.map(w => ({ value: w.scenarioId, label: w.label })), [weeks]);
  const playerOptions = useMemo(() => players.map(p => ({ value: p.id, label: p.name })), [players]);

  const handleExportPdf = () => {
    if (!selectedPlayerIds.length || !selectedScenarioIds.length) return;
    localStorage.setItem('print_individual_progress', JSON.stringify({
      playerIds: selectedPlayerIds,
      scenarioIds: selectedScenarioIds,
      hiddenLines: [...hiddenLines],
    }));
    window.open('/print/individual-progress', '_blank');
  };

  const ready = !loading;
  const canExport = selectedPlayerIds.length > 0 && selectedScenarioIds.length > 0;

  return (
    <PageChrome onNavigate={onNavigate} user={user}>
      <div style={{ maxWidth: 1200, margin: '0 auto', paddingBottom: 40 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, gap: 16 }}>
          <div>
            <h2 style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Individual Progress Report</h2>
            <p style={{ fontSize: '0.83rem', color: 'var(--text-muted)', margin: '4px 0 0' }}>
              Physical/Technical/Tactical/Identity/Availability/Overall, one number per selected week (Physical uses that week's own average EWMA, not a live snapshot).
            </p>
          </div>
          <img src={`${process.env.PUBLIC_URL}/heracles-crest.png`} alt="Heracles Almelo" style={{ height: 44, width: 'auto', opacity: 0.85 }} />
        </div>

        {!ready ? (
          <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>Loading…</p>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
              <MultiSelect options={playerOptions} selected={selectedPlayerIds} onChange={setSelectedPlayerIds} placeholder="Select player(s)…" minWidth={220} />
              <MultiSelect options={weekOptions} selected={selectedScenarioIds} onChange={setSelectedScenarioIds} placeholder="Select week(s)…" minWidth={260} />
              <div style={{ marginLeft: 'auto' }}>
                <button
                  onClick={handleExportPdf}
                  disabled={!canExport}
                  style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '7px 14px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'transparent', color: canExport ? 'var(--text-secondary)' : 'var(--text-dimmed)', cursor: canExport ? 'pointer' : 'not-allowed', fontSize: '0.83rem', fontWeight: 600, fontFamily: 'inherit' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                  PDF Export
                </button>
              </div>
            </div>

            {!selectedPlayerIds.length || !selectedScenarioIds.length ? (
              <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: 48, textAlign: 'center', color: 'var(--text-dimmed)' }}>
                <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                  Select at least one player and one week to see progress.
                </p>
              </div>
            ) : (
              <>
                <MetricLegend hidden={hiddenLines} onToggle={toggleLine} />
                {loadingScores ? (
                  <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>Loading scores…</p>
                ) : (
                  <>
                    <PlayerLegend players={selectedPlayersOrdered} />
                    <CombinedChart
                      players={selectedPlayersOrdered}
                      weeks={selectedWeeksOrdered}
                      scoresByPlayerAndWeek={scoresByPlayerAndWeek}
                      hiddenLines={hiddenLines}
                    />
                    {selectedPlayersOrdered.map(player => (
                      <PlayerTableSection
                        key={player.id}
                        player={player}
                        weeks={selectedWeeksOrdered}
                        scoresByPlayer={scoresByPlayerAndWeek[player.id] || {}}
                        hiddenLines={hiddenLines}
                      />
                    ))}
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    </PageChrome>
  );
}
